import { ImagePrintDetail } from 'editor/src/store/gallery/types';

import measureGradientShare from './measureGradientShare';
import { RasterPixels } from './types';

const ALPHA_VISIBLE = 13;
export const ALPHA_SOLID = 128;
const ALPHA_STRONG_OPAQUE = 230;

// Opening widths (raster pixels) measured; a width in between reads the next smaller one, so a
// check under-reports rather than flags a part that is wide enough. The wide ones are measured on
// the mask pooled by `factor` (solid where any pixel under it is), at a fraction of a full pass:
// pooling only widens a part, so they under-report too.
const OPENING_LEVELS = [
  { factor: 1, widths: [2, 3, 4, 6, 8, 12] },
  { factor: 2, widths: [8, 12] },
  { factor: 4, widths: [8, 12] },
];
export const MAX_MEASURED_WIDTH = Math.max(
  ...OPENING_LEVELS.map(({ factor, widths }) => factor * widths[widths.length - 1]),
);

// A square of side k only fits a 45° line about 1.41·k wide; a diamond reaching across this many
// pixel diagonals fits a 45° line k wide. A part is thin only when neither shape fits in it.
const diamondRadius = (k: number) => Math.round((k * Math.SQRT2 - 1) / 2);
const FAR = 0xffff;
// A corner narrows below a width k only within about this many k of its point.
const CORNER_REACH = 1.5;
// A thin part no longer than this many k across is what an opening leaves at a turned corner (up to
// about 1k measured); a spur or a fringe tooth worth flagging runs longer (from about 1.2k).
const COMPACT_EXTENT = 1.1;
// Chamfer steps for distances along the artwork: a diagonal step counts about √2 straight ones.
const STRAIGHT_STEP = 2;
const DIAGONAL_STEP = 3;

// Raster-sized buffers shared by every pass of one analysis, instead of a fresh set per pass.
interface Scratch {
  first: Uint8Array;
  second: Uint8Array;
  prefix: Int32Array;
  // city-block distance from each solid pixel to the nearest pixel outside the artwork
  insideDistance: Uint16Array;
  distance: Uint16Array;
}

// Writes 1 to `out` where every pixel of the k-wide window starting `offset` before it is 1, or,
// when dilating, where any pixel of the mirrored window is 1. Outside the raster counts as 0.
function boxPass(
  src: Uint8Array,
  out: Uint8Array,
  prefix: Int32Array,
  width: number,
  height: number,
  k: number,
  horizontal: boolean,
  dilate: boolean,
): void {
  const lineCount = horizontal ? height : width;
  const lineLength = horizontal ? width : height;
  const step = horizontal ? 1 : width;
  const before = Math.floor((k - 1) / 2);
  const from = dilate ? -(k - 1 - before) : -before;
  const to = dilate ? before : k - 1 - before;

  for (let line = 0; line < lineCount; line += 1) {
    const start = horizontal ? line * width : line;
    for (let i = 0; i < lineLength; i += 1) {
      prefix[i + 1] = prefix[i] + src[start + i * step];
    }
    for (let i = 0; i < lineLength; i += 1) {
      const lo = i + from;
      const hi = i + to;
      if (dilate) {
        const sum = prefix[Math.min(hi, lineLength - 1) + 1] - prefix[Math.max(lo, 0)];
        out[start + i * step] = sum > 0 ? 1 : 0;
      } else {
        out[start + i * step] = lo >= 0 && hi < lineLength && prefix[hi + 1] - prefix[lo] === k ? 1 : 0;
      }
    }
  }
}

// Leaves in `scratch.second` the pixels a k×k square covers while staying inside the mask.
function openWithSquare(mask: Uint8Array, scratch: Scratch, width: number, height: number, k: number): void {
  const { first, second, prefix } = scratch;
  boxPass(mask, first, prefix, width, height, k, true, false);
  boxPass(first, second, prefix, width, height, k, false, false);
  boxPass(second, first, prefix, width, height, k, true, true);
  boxPass(first, second, prefix, width, height, k, false, true);
}

// Two-pass city-block distance to the nearest source pixel (distance 0). `outside` is the distance
// a pixel beyond the raster edge has: 0 when the edge counts as a source, FAR when it does not.
function cityBlockDistance(distance: Uint16Array, width: number, height: number, outside: number): void {
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const p = y * width + x;
      if (distance[p]) {
        const up = y > 0 ? distance[p - width] : outside;
        const left = x > 0 ? distance[p - 1] : outside;
        distance[p] = Math.min(distance[p], up + 1, left + 1);
      }
    }
  }
  for (let y = height - 1; y >= 0; y -= 1) {
    for (let x = width - 1; x >= 0; x -= 1) {
      const p = y * width + x;
      if (distance[p]) {
        const down = y < height - 1 ? distance[p + width] : outside;
        const right = x < width - 1 ? distance[p + 1] : outside;
        distance[p] = Math.min(distance[p], down + 1, right + 1);
      }
    }
  }
}

// `visited` marks of a thin pixel: taken into its part, touching the wide part, and walked as such
const PART = 1;
const BORDER = 2;
const WALKED = 3;
const NEIGHBOURS = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

/**
 * Weighs the thin pixels (solid, not `wide`) no corner explains. A connected thin part touching the
 * wide part is a corner when it meets it across more than k/2 pixels (a pointed corner narrowing
 * away from it) or fits within COMPACT_EXTENT·k (the sliver left at a turned square corner); a
 * corner counts only beyond CORNER_REACH·k from the wide part. A hairline, a spur or a fringe meets
 * the wide part across its own few pixels and runs longer, so it counts whole.
 */
function weighUnexplainedThin(
  solid: Uint8Array,
  wide: Uint8Array,
  visited: Uint8Array,
  distance: Uint16Array,
  weight: Float32Array,
  width: number,
  height: number,
  k: number,
): number {
  const maxDistance = Math.round(CORNER_REACH * k * STRAIGHT_STEP);
  const isThin = (p: number) => solid[p] === 1 && wide[p] === 0;
  const buckets: number[][] = Array.from({ length: maxDistance + 1 }, () => []);
  visited.fill(0);
  let unexplained = 0;

  for (let start = 0; start < solid.length; start += 1) {
    if (isThin(start) && !visited[start]) {
      visited[start] = PART;
      const part = [start];
      const border: number[] = [];
      let left = width;
      let right = 0;
      let top = height;
      let bottom = 0;
      for (let i = 0; i < part.length; i += 1) {
        const p = part[i];
        const x = p % width;
        const y = (p - x) / width;
        left = Math.min(left, x);
        right = Math.max(right, x);
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
        distance[p] = FAR;
        let touchesWide = false;
        for (let j = 0; j < NEIGHBOURS.length; j += 1) {
          const nx = x + NEIGHBOURS[j][0];
          const ny = y + NEIGHBOURS[j][1];
          if (nx >= 0 && ny >= 0 && nx < width && ny < height) {
            const n = ny * width + nx;
            if (wide[n]) {
              touchesWide = true;
            } else if (isThin(n) && !visited[n]) {
              visited[n] = PART;
              part.push(n);
            }
          }
        }
        if (touchesWide) {
          visited[p] = BORDER;
          border.push(p);
        }
      }

      const compact = Math.hypot(right - left + 1, bottom - top + 1) <= COMPACT_EXTENT * k;
      const partWeight = part.reduce((sum, p) => sum + weight[p], 0);
      if (border.length && (compact || hasRunLongerThan(border, visited, width, height, k / 2))) {
        unexplained += partWeight - weighWithinReach(border, isThin, weight, distance, buckets, width, height);
      } else {
        unexplained += partWeight;
      }
    }
  }
  return unexplained;
}

// Whether more than `limit` of the border pixels join up, 8-connected. Marks the ones it walks.
function hasRunLongerThan(
  border: number[],
  visited: Uint8Array,
  width: number,
  height: number,
  limit: number,
): boolean {
  for (let b = 0; b < border.length; b += 1) {
    if (visited[border[b]] === BORDER) {
      visited[border[b]] = WALKED;
      const run = [border[b]];
      for (let i = 0; i < run.length; i += 1) {
        const x = run[i] % width;
        const y = (run[i] - x) / width;
        for (let j = 0; j < NEIGHBOURS.length; j += 1) {
          const nx = x + NEIGHBOURS[j][0];
          const ny = y + NEIGHBOURS[j][1];
          const n = ny * width + nx;
          if (nx >= 0 && ny >= 0 && nx < width && ny < height && visited[n] === BORDER) {
            visited[n] = WALKED;
            run.push(n);
          }
        }
      }
      if (run.length > limit) {
        return true;
      }
    }
  }
  return false;
}

// Chamfer distance along the thin part from the pixels touching the wide part, up to the last
// bucket; returns the weight of the pixels within it. Leaves the buckets empty.
function weighWithinReach(
  border: number[],
  isThin: (p: number) => boolean,
  weight: Float32Array,
  distance: Uint16Array,
  buckets: number[][],
  width: number,
  height: number,
): number {
  const maxDistance = buckets.length - 1;
  border.forEach((p) => {
    distance[p] = STRAIGHT_STEP;
    buckets[STRAIGHT_STEP].push(p);
  });

  let within = 0;
  for (let d = STRAIGHT_STEP; d <= maxDistance; d += 1) {
    const bucket = buckets[d];
    for (let i = 0; i < bucket.length; i += 1) {
      const p = bucket[i];
      if (distance[p] === d) {
        within += weight[p];
        const x = p % width;
        const y = (p - x) / width;
        for (let j = 0; j < NEIGHBOURS.length; j += 1) {
          const [dx, dy] = NEIGHBOURS[j];
          const nx = x + dx;
          const ny = y + dy;
          if (nx >= 0 && ny >= 0 && nx < width && ny < height) {
            const n = ny * width + nx;
            const next = d + (dx && dy ? DIAGONAL_STEP : STRAIGHT_STEP);
            if (next <= maxDistance && isThin(n) && next < distance[n]) {
              distance[n] = next;
              buckets[next].push(n);
            }
          }
        }
      }
    }
    bucket.length = 0;
  }
  return within;
}

function createScratch(mask: Uint8Array, width: number, height: number): Scratch {
  const pixelCount = width * height;
  const insideDistance = new Uint16Array(pixelCount);
  for (let p = 0; p < pixelCount; p += 1) {
    insideDistance[p] = mask[p] ? FAR : 0;
  }
  cityBlockDistance(insideDistance, width, height, 0);
  return {
    first: new Uint8Array(pixelCount),
    second: new Uint8Array(pixelCount),
    prefix: new Int32Array(Math.max(width, height) + 1),
    insideDistance,
    distance: new Uint16Array(pixelCount),
  };
}

// The mask at 1/factor of its size, a pixel solid where any pixel it covers is, weighing what the
// pixels it covers weigh, so a part pooling widens still counts only its own area.
function poolMask(mask: Uint8Array, weight: Float32Array, width: number, height: number, factor: number) {
  const pooledWidth = Math.ceil(width / factor);
  const pooledHeight = Math.ceil(height / factor);
  const pooled = new Uint8Array(pooledWidth * pooledHeight);
  const pooledWeight = new Float32Array(pooled.length);
  let solidCount = 0;
  for (let y = 0; y < height; y += 1) {
    const row = Math.floor(y / factor) * pooledWidth;
    for (let x = 0; x < width; x += 1) {
      const p = row + Math.floor(x / factor);
      if (mask[y * width + x]) {
        pooledWeight[p] += weight[y * width + x];
        if (!pooled[p]) {
          pooled[p] = 1;
          solidCount += 1;
        }
      }
    }
  }
  return { mask: pooled, weight: pooledWeight, width: pooledWidth, height: pooledHeight, solidCount };
}

// The weight of the solid pixels neither a k×k square nor the matching diamond covers while staying
// inside the artwork, and no corner explains.
function weighThin(
  solid: Uint8Array,
  weight: Float32Array,
  scratch: Scratch,
  width: number,
  height: number,
  k: number,
): number {
  openWithSquare(solid, scratch, width, height, k);
  const { first: wide, second: squareOpened, insideDistance, distance } = scratch;
  const radius = diamondRadius(k);
  for (let p = 0; p < distance.length; p += 1) {
    distance[p] = insideDistance[p] > radius ? 0 : FAR;
  }
  cityBlockDistance(distance, width, height, FAR);

  let thinWeight = 0;
  let wideCount = 0;
  for (let p = 0; p < solid.length; p += 1) {
    wide[p] = solid[p] && (squareOpened[p] || distance[p] <= radius) ? 1 : 0;
    wideCount += wide[p];
    if (solid[p] && !wide[p]) {
      thinWeight += weight[p];
    }
  }
  if (!thinWeight || !wideCount) {
    return thinWeight;
  }
  return weighUnexplainedThin(solid, wide, squareOpened, distance, weight, width, height, k);
}

type PrintDetailResult = Omit<ImagePrintDetail, 'url'>;

/**
 * Measures, on an image's own pixels, what the DTF and embroidery print checks look at: soft
 * semi-transparent areas (not the one-pixel anti-aliased edge every cut-out has), smooth colour
 * gradients, and how much of the artwork sits in parts too thin to survive printing.
 * Gradients are read on `gradientRaster`, the size the gradient thresholds are calibrated for, and
 * the artwork's solid parts on `solidRaster` (see RasterPixels.coverage): a pixel is solid where
 * any source pixel it covers is, so a hairline narrower than a pixel is not lost, and weighs only
 * the share of it that is, so a speck does not count as a whole pixel.
 * Pauses after each pass, so a caller can hand the main thread back between them.
 */
export function* analyzePrintDetailSteps(
  raster: RasterPixels,
  gradientRaster: RasterPixels,
  solidRaster: RasterPixels = raster,
): Generator<void, PrintDetailResult, void> {
  const { data, width, height } = raster;
  const pixelCount = width * height;
  const semi = new Uint8Array(pixelCount);
  const solid = new Uint8Array(pixelCount);
  const weight = new Float32Array(pixelCount);
  let visible = 0;
  let solidCount = 0;
  let totalWeight = 0;

  for (let p = 0; p < pixelCount; p += 1) {
    const alpha = data[p * 4 + 3];
    if (alpha > ALPHA_VISIBLE) {
      visible += 1;
      if (alpha < ALPHA_STRONG_OPAQUE) {
        semi[p] = 1;
      }
    }
    if (solidRaster.data[p * 4 + 3] >= ALPHA_SOLID) {
      solid[p] = 1;
      solidCount += 1;
      weight[p] = solidRaster.coverage?.[p] ?? 1;
      totalWeight += weight[p];
    }
  }

  let softPixels = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const p = y * width + x;
      if (
        semi[p] &&
        semi[p - width - 1] &&
        semi[p - width] &&
        semi[p - width + 1] &&
        semi[p - 1] &&
        semi[p + 1] &&
        semi[p + width - 1] &&
        semi[p + width] &&
        semi[p + width + 1]
      ) {
        softPixels += 1;
      }
    }
  }

  yield;

  const gradientRatio = measureGradientShare(gradientRaster);
  yield;

  const thinRatioByWidth: number[] = [];
  if (solidCount > 0 && solidCount < pixelCount) {
    const measured: number[] = [];
    for (let l = 0; l < OPENING_LEVELS.length; l += 1) {
      const { factor, widths } = OPENING_LEVELS[l];
      const level =
        factor === 1
          ? { mask: solid, weight, width, height, solidCount }
          : poolMask(solid, weight, width, height, factor);
      // pooled shut, it has no opening left to tell; its widths read the last one measured
      if (level.solidCount < level.width * level.height) {
        const scratch = createScratch(level.mask, level.width, level.height);
        yield;
        for (let w = 0; w < widths.length; w += 1) {
          measured[widths[w] * factor] =
            weighThin(level.mask, level.weight, scratch, level.width, level.height, widths[w]) / totalWeight;
          yield;
        }
      }
    }

    // A part thinner than k is thinner than any wider width too, while pooling can close the gaps of
    // a fine texture and read none: each width keeps at least the share of the narrower ones.
    let ratio = 0;
    for (let k = 0; k <= MAX_MEASURED_WIDTH; k += 1) {
      ratio = Math.max(ratio, measured[k] ?? 0);
      thinRatioByWidth.push(ratio);
    }
  }

  return {
    semiTransparentRatio: visible ? softPixels / visible : 0,
    gradientRatio,
    rasterWidth: width,
    thinRatioByWidth,
  };
}

function analyzePrintDetail(
  raster: RasterPixels,
  gradientRaster: RasterPixels,
  solidRaster?: RasterPixels,
): PrintDetailResult {
  const steps = analyzePrintDetailSteps(raster, gradientRaster, solidRaster);
  let step = steps.next();
  while (!step.done) {
    step = steps.next();
  }
  return step.value;
}

export default analyzePrintDetail;
